import type { AccountDevice } from '../transport/api'

/**
 * WHY A MACHINE IS NOT ON THE CURRENT BUILD, in one line a person can act on.
 *
 * The owner's machines list said "(out of date)" and nothing else, and two machines sat
 * there for days — avg-main could not ssh into either, so nobody could see why. The
 * edge now reports `update_health` on its link frame, and this turns that into the
 * sentence and the tone the row shows.
 *
 * THE FIRST REAL READING IS WHY THIS IS NOT JUST `blocked`. On 2026-08-22, with the
 * channel at 8e76ff3:
 *
 *   aowl        running=49a59bd  available=null      updater=off  blocked=null
 *   tbc-m1      running=49a59bd  available=49a59bd   updater=on   blocked=null
 *   yy-a        running=8e76ff3  available=null      updater=on   blocked=null
 *   spark-244c  running=49a59bd  available=49a59bd   updater=on   blocked=null
 *
 * The two stuck machines are NOT blocked. Their updater is on, they report no error,
 * and they believe `available` is the build they are already running — they are being
 * told there is nothing to update to. A row that trusted `blocked` would have drawn
 * both of them as healthy, which is exactly the silence this feature exists to end.
 *
 * So BEHIND IS COMPUTED HERE, from the cloud's two facts (what the machine says it is
 * running, and what the channel is serving), never from the machine's own opinion of
 * whether it is current. avg-main's rule, and this reading is its proof: a machine that
 * cannot see the new build cannot know it is behind, and the cloud can.
 *
 * `updater=off` IS NOT AN ERROR. aowl was taken off auto-update deliberately on
 * 2026-08-20 (11 restarts a day read to the owner as "連線不穩定"). Drawing every
 * deliberately-pinned machine in red would train people to ignore the colour.
 */

export type UpdateHealth = {
  /** The build the PROCESS is running — not what is on disk. */
  running?: string | null
  /** The newest build this machine has SEEN on its channel. Its opinion, not a fact. */
  available?: string | null
  channel?: string | null
  channel_source?: string | null
  updater?: 'on' | 'off' | string | null
  /** null = switched off deliberately; a string = it wants to update and cannot. */
  off_reason?: string | null
  supervised?: boolean | null
  node_major?: number | null
  last_error?: string | null
  last_error_at?: number | null
  last_ok?: string | null
  last_ok_at?: number | null
  /** A machine-readable cause. null does NOT mean healthy — see the reading above. */
  blocked?: string | null
  /** The machine's own sentence for `blocked`, shown verbatim. */
  blocked_reason?: string | null
}

export type UpdateTone =
  /** On the current build, nothing to say. */
  | 'ok'
  /** True and deliberate, or simply unknown — worth showing, not worth alarming. */
  | 'note'
  /** Something is wrong and a person can do something about it. */
  | 'warn'

export type UpdateStatus = {
  tone: UpdateTone
  /** One line for the row. Empty only when `tone` is 'ok'. */
  line: string
  /** Show the Update button — false where pressing it cannot help. */
  offerUpdate: boolean
}

/** `update_health` off a device row, or null when this build never reported one. */
export function healthOf(device: Pick<AccountDevice, 'device_id'> & { update_health?: unknown }): UpdateHealth | null {
  const h = device.update_health
  return h && typeof h === 'object' ? (h as UpdateHealth) : null
}

/**
 * What the row should say about this machine's build.
 *
 * `channel` is what the app's own origin is serving — the same value the version badge
 * compares against, so the two can never disagree.
 */
/**
 * "It last updated itself on …", when the machine can say so.
 *
 * ABSENCE SAYS NOTHING HERE, deliberately, and this is the second time today the same
 * discipline has been needed. avg-main's persistence fix (8b6b93c) makes `last_ok`
 * survive the restart, and his framing is that a null then means auto-update has never
 * succeeded on that machine. That becomes true only once a machine is RUNNING the build
 * that persists it — and until the fleet gets there, every machine reads null, including
 * yy-a, which demonstrably updates itself.
 *
 * So this never turns an absence into a claim. It only enriches a verdict already
 * reached on other evidence: when there IS a date, say it; when there is not, say
 * nothing rather than "never".
 */
function lastOkSuffix(health: UpdateHealth): string {
  const at = health.last_ok_at
  if (typeof at !== 'number' || !Number.isFinite(at) || at <= 0) return ''
  // Seconds or milliseconds — the wire has carried both shapes elsewhere in this app.
  const ms = at > 1e12 ? at : at * 1000
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return ''
  return ` It last updated itself on ${d.toLocaleDateString()}.`
}

/**
 * WHAT THIS MACHINE IS RUNNING — one answer, for every part of the row.
 *
 * Owner, 2026-09-17: gmi's row said "7e92cb4 (out of date)" beside a status computed
 * from 7148dee, because the badge read `device.version` and the status read
 * `update_health.running`. The EDGE sends one value for both (cloudLink.ts:153 is
 * literally `version: getUpdateHealth().running`), so the two can only differ in the
 * cloud — update_health is pushed when it changes, `version` only when a frame carries
 * it — and the row then drew the disagreement as if it were two facts.
 *
 * The health snapshot wins because it is the one that is stamped: `update_health_at`
 * says when it was taken, and `version` carries no such mark. Whichever is fresher, a
 * row must not say two things about one machine.
 */
export function runningOf(
  health: UpdateHealth | null,
  reportedVersion: string | undefined,
): string {
  return (health?.running || reportedVersion || '').trim()
}

export function updateStatus(
  health: UpdateHealth | null,
  reportedVersion: string | undefined,
  channel: string | null,
): UpdateStatus {
  const running = runningOf(health, reportedVersion)
  const behind = !!channel && !!running && running !== channel

  /*
   * NO REPORT AT ALL. Absence is not health and it is not a fault: a build older than
   * the field says nothing, and the machines most likely to be stuck are exactly the
   * ones too old to speak. Only worth a line when the machine is ALSO behind — a
   * current machine that happens to be quiet is not news, and drawing it would put a
   * notice on every row for nothing.
   */
  if (!health) {
    return behind
      ? { tone: 'note', line: 'This build does not report why — it may be too old to say.', offerUpdate: true }
      : { tone: 'ok', line: '', offerUpdate: false }
  }

  /*
   * `updater = off` COMES FIRST, and reading the live screen is what found this.
   *
   * aowl is the machine the owner deliberately took off auto-update on 2026-08-20, and
   * the rail drew it as "update stuck — tap for why" — because it also carries
   * `blocked = pending`, and the named-cause branch below used to run first. An off
   * machine seeing a new version it is not going to install is not stuck; being off IS
   * the story, and it is the one this tone exists to tell quietly. Getting it wrong on
   * exactly the machine the `note` tone was designed for is the kind of thing only the
   * screen says.
   */
  if (health.updater === 'off' && !health.off_reason) {
    return {
      tone: 'note',
      line: behind
        ? 'Auto-update is off — this machine will not update itself.'
        : 'Auto-update is off.',
      offerUpdate: behind,
    }
  }

  /*
   * The OTHER off, and it is a fault wearing the same word: a reason means the machine
   * WANTS to update and cannot. It sits here, above `pending`, because a machine that
   * cannot update is not made well by also having one queued.
   */
  if (health.updater === 'off' && health.off_reason) {
    return { tone: 'warn', line: health.off_reason, offerUpdate: false }
  }

  /*
   * `pending` IS NOT A FAULT EITHER. It means the machine has seen a newer build and
   * has one queued — the state every healthy machine passes through on its way to
   * current. Drawing it in the same colour as `install_failing` is a lying label, and
   * it would flash red across the whole fleet on every publish.
   */
  if (health.blocked === 'pending') {
    return { tone: 'note', line: 'An update is on its way.', offerUpdate: false }
  }

  // A named cause is the machine telling us, in its own words. Nothing beats it.
  if (health.blocked) {
    return {
      tone: 'warn',
      line: health.blocked_reason?.trim() || `Cannot update: ${health.blocked}`,
      /*
       * `restart_pending` is the one blocked state a press CAN clear — the build is
       * installed and the process has not swapped. The rest are refusals that a second
       * identical request will meet again.
       */
      offerUpdate: health.blocked === 'restart_pending',
    }
  }


  /*
   * THE STATE THE FIRST REAL READING FOUND, and the one nothing else covers: the
   * updater is on, the machine reports no fault, and it is still behind. Its own
   * `available` says it is current, so it is not going to try. Saying "up to date"
   * because the machine said so is how tbc-m1 and spark-244c sat unnoticed.
   */
  if (behind) {
    const thinksCurrent = !!health.available && health.available === running
    return {
      tone: 'warn',
      line:
        (thinksCurrent
          ? 'This machine thinks it is up to date — it is not seeing the new build.'
          : 'Behind, and it has not updated itself yet.') + lastOkSuffix(health),
      offerUpdate: true,
    }
  }

  return { tone: 'ok', line: '', offerUpdate: false }
}
